import {inject, Injectable} from '@angular/core';
import {filter, first, map, merge, Observable, switchMap, tap} from 'rxjs';
import {v4 as uuidv4} from 'uuid';

import {PaymentAttemptedEvent} from '../../../../../shared/application/events/payment/payment-attempted.event';
import {PAYMENT_METHOD} from '../../../../../shared/enums/payment-method.enum';
import {PayBookingAccountUseCase} from '../../../payment/application/use-cases/pay-booking-account.usecase';
import {EntryViewerStateHandler} from './state.handler';
import {EVENT_BUS} from '../../../../../shared/domain/tokens/event-bus.token';
import {BookingLockCheckedEvent} from '../../../../../shared/application/events/booking/booking-lock-checked.event';
import {Booking} from '../../../booking/domain/models/booking.model';
import {IPaymentResult} from '../../../payment/domain/interfaces/payment-storage.interface';

@Injectable({ providedIn: 'root' })
export class EntryViewerPayBookingHandler {
  private readonly payUseCase = inject(PayBookingAccountUseCase);
  private readonly eventBus = inject(EVENT_BUS);
  private readonly stateHandler = inject(EntryViewerStateHandler);

  payBookingAccount(paymentMethod: PAYMENT_METHOD): Observable<IPaymentResult> {
    const correlationId = uuidv4();

    return this.getActiveBookingId().pipe(
      first(),
      tap(bookingId => this.eventBus.publish(new PaymentAttemptedEvent({ bookingId, correlationId }))),
      switchMap(() => this.waitForBookingLock(correlationId)),
      switchMap(() => this.payUseCase.execute(paymentMethod))
    );
  }

  private getActiveBookingId(): Observable<string> {
    return this.stateHandler.activeBooking$.pipe(
      filter((booking): booking is Booking => !!booking?.id),
      map(booking => booking.id)
    );
  }

  private waitForBookingLock(correlationId: string): Observable<boolean> {
    return merge(
      this.eventBus.ofType(BookingLockCheckedEvent).pipe(
        filter(e => e.payload.correlationId === correlationId),
        map((e) => !e.payload.isLocked)
      ),
    ).pipe(
      first()
    );
  }
}
